/**
 * Profile-aware job query building and seniority filtering for the AI Agent.
 */
import {
  experienceBucket,
  yearsToBucket,
} from '../../dashboard/components/Jobs/ranking';
import {
  classifyJobFunction,
  roleAlignmentScore,
} from '../../recommendations/engine/scoring/dimensionScores';

export type ProfileMatchContext = {
  currentRole?: string | null;
  headline?: string | null;
  skills?: string[] | null;
  experienceYears?: number | null;
  location?: string | null;
};

type JobLike = {
  title?: string | null;
  experience_level?: string | null;
  description_md?: string | null;
  description?: string | null;
  location?: string | null;
  matchScore?: number;
};

const QUERY_STOP = new Set([
  'find', 'show', 'search', 'jobs', 'job', 'role', 'roles', 'for', 'the', 'and', 'with',
  'that', 'match', 'matching', 'recommended', 'profile', 'based', 'senior', 'lead',
  'principal', 'director', 'head', 'top', 'some', 'your', 'right', 'now', 'let', 'me',
  'awesome', 'great', 'sure', 'please', 'want', 'need', 'looking', 'available', 'openings',
  'position', 'positions', 'bengaluru', 'bangalore', 'mumbai', 'delhi', 'hyderabad',
  'chennai', 'pune', 'india', 'remote',
]);

const LOCATION_ALIASES: Record<string, string[]> = {
  bengaluru: ['bengaluru', 'bangalore', 'blr'],
  bangalore: ['bangalore', 'bengaluru', 'blr'],
  mumbai: ['mumbai', 'bombay'],
  delhi: ['delhi', 'ncr', 'gurugram', 'gurgaon', 'noida', 'new delhi'],
  hyderabad: ['hyderabad', 'secunderabad'],
  chennai: ['chennai', 'madras'],
  pune: ['pune', 'pimpri'],
};

const JUNIOR_TITLE_RE =
  /\b(junior|jr\.?\b|entry[\s-]level|intern(ship)?|trainee|fresher|graduate trainee|campus hire|0\s*[-–]\s*[12]\s*(yr|year|years)|1\s*[-–]\s*2\s*(yr|year|years))\b/i;

const EXEC_TITLE_RE =
  /\b(director|vp\b|svp|evp|c[eo]o|chief|head of|group product|principal|staff product|executive)\b/i;

const IC_EXECUTION_ONLY_RE =
  /\b(frontend developer|backend developer|full[\s-]?stack developer|software engineer i\b|sde[\s-]?1|sde[\s-]?2|qa engineer|test engineer|data entry)\b/i;

function inferJobBucketFromTitle(title: string): number | null {
  const t = title.toLowerCase();
  if (EXEC_TITLE_RE.test(t)) return 5;
  if (/\b(senior|sr\.?|lead)\b/.test(t)) return 4;
  if (/\b(mid[\s-]level|intermediate)\b/.test(t)) return 3;
  if (JUNIOR_TITLE_RE.test(t)) return 1;
  return null;
}

/** Minimum acceptable job seniority bucket given user experience years. */
export function minimumJobBucketForUser(years: number | null | undefined): number | null {
  if (years == null) return null;
  if (years >= 15) return 4; // Senior+ only; director/exec preferred
  if (years >= 10) return 3; // Mid-senior floor
  if (years >= 6) return 2;
  if (years >= 3) return 1;
  return 0;
}

export function isJuniorOrEntryJob(job: JobLike): boolean {
  const title = (job.title ?? '').toLowerCase();
  const level = (job.experience_level ?? '').toLowerCase();
  if (JUNIOR_TITLE_RE.test(title)) return true;
  if (/intern|entry|fresher|trainee|0\s*[-–]\s*1|1\s*[-–]\s*2/.test(level)) return true;
  if (/\bassociate\b/.test(title) && !/\b(senior|director|manager)\b/.test(title)) return true;
  return false;
}

export function jobMeetsSeniorityFloor(job: JobLike, profile: ProfileMatchContext): boolean {
  const years = profile.experienceYears;
  const minBucket = minimumJobBucketForUser(years);
  if (minBucket == null || minBucket < 3) return true;

  if (isJuniorOrEntryJob(job)) return false;

  // Heavy IC execution roles rarely fit 10+ YOE product leaders
  if (years != null && years >= 10 && IC_EXECUTION_ONLY_RE.test(job.title ?? '')) {
    return false;
  }

  const jobBucket =
    experienceBucket(job.experience_level) ?? inferJobBucketFromTitle(job.title ?? '');

  if (jobBucket == null) return true;

  const userBucket = years != null ? yearsToBucket(years) : null;
  if (userBucket == null) return jobBucket >= minBucket;

  // Job more than 2 seniority tiers below the user → drop
  const gap = userBucket - jobBucket;
  if (gap >= 3) return false;

  return jobBucket >= minBucket;
}

/** Seniority tokens appended to retrieval queries for experienced profiles. */
export function seniorityQueryTokens(years: number | null | undefined, role?: string | null): string {
  const r = (role ?? '').toLowerCase();
  const isProduct = /product|pm\b/.test(r);

  if (years != null && years >= 15) {
    return isProduct
      ? 'Director VP Head of Product Principal Product Leader Executive'
      : 'Director VP Principal Head Senior Executive Leadership';
  }
  if (years != null && years >= 10) {
    return isProduct
      ? 'Senior Product Manager Lead Product Principal Group Product'
      : 'Senior Lead Principal Staff Director';
  }
  if (years != null && years >= 6) {
    return 'Senior Lead';
  }
  return '';
}

/**
 * Detect whether the user's intent contains explicit role/technology keywords
 * that differ from their profile role (e.g. profile="Product Manager" but
 * user typed "java developer jobs"). In that case, we should NOT mix in
 * profile role tokens — just use the user's keywords.
 */
const EXPLICIT_ROLE_KEYWORDS_RE =
  /\b(\.net|dotnet|java\b|python|react|angular|vue|node|golang|go\b|rust|ruby|php|swift|kotlin|flutter|devops|sre|data\s*engineer|data\s*scientist|machine\s*learning|ml\b|ai\b|cloud|aws|azure|gcp|frontend|backend|full[\s-]?stack|ios|android|mobile|qa|test|blockchain|cyber[\s-]?security|salesforce|sap\b|power\s*bi|tableau|sql|database|dba|network|infrastructure|embedded|firmware|hardware|ux|ui\s*design|graphic\s*design|content\s*writ|copywriter|marketing|sales|hr\b|recruit|finance|account|legal|medical|nursing|pharma|teaching|chef|hospitality)/i;

function hasExplicitRoleKeywords(text: string): boolean {
  return EXPLICIT_ROLE_KEYWORDS_RE.test(text);
}

function userIntentDiffersFromProfile(intent: string, profileRole: string): boolean {
  if (!profileRole || !intent) return false;
  const intentLower = intent.toLowerCase();
  const roleLower = profileRole.toLowerCase();
  const roleFirstWord = roleLower.split(/\s+/)[0] ?? '';
  if (intentLower.includes(roleFirstWord)) return false;
  return hasExplicitRoleKeywords(intent);
}

export function buildProfileAwareJobQuery(
  userIntent: string,
  profile: ProfileMatchContext,
): string {
  const role = profile.currentRole?.trim() || profile.headline?.trim() || '';
  const trimmedIntent = userIntent.trim();
  const base = trimmedIntent || role || 'jobs';

  // When user explicitly searches for a different role/tech than their profile,
  // prioritize user intent — don't pollute with profile role tokens.
  const isExplicitDifferentSearch = trimmedIntent && userIntentDiffersFromProfile(trimmedIntent, role);

  const parts: string[] = [];

  if (isExplicitDifferentSearch) {
    parts.push(base);
    const city = profile.location?.trim().split(',')[0]?.trim();
    if (city) parts.push(city);
  } else {
    if (role && !base.toLowerCase().includes(role.toLowerCase().split(/\s+/)[0] ?? '')) {
      parts.push(role);
    }
    parts.push(base);

    const seniority = seniorityQueryTokens(profile.experienceYears, role);
    if (seniority) parts.push(seniority);

    const skills = (profile.skills ?? []).slice(0, 3).join(' ');
    if (skills) parts.push(skills);

    const city = profile.location?.trim().split(',')[0]?.trim();
    if (city) parts.push(city);
  }

  return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

export function buildJobMatchingSystemHint(profile: ProfileMatchContext | null): string {
  if (!profile?.currentRole && !profile?.experienceYears) {
    return [
      'JOB MATCHING: User profile is not fully configured.',
      'IMPORTANT: Do NOT block the user from searching. If they ask for specific roles/technologies (e.g. ".NET jobs", "Java developer", "data engineer"), search IMMEDIATELY with their exact keywords.',
      'Only ask clarifying questions if the request is truly vague (e.g. just "find me jobs" with no specifics).',
      'The user can explore ANY role — do not restrict to their profile domain.',
    ].join('\n');
  }

  const role = profile.currentRole ?? profile.headline ?? 'their field';
  const years = profile.experienceYears ?? 'unknown';

  let seniorityRule =
    'Prefer roles aligned with the user\'s seniority and domain, but ALLOW the user to explore different roles if they explicitly ask.';
  if (typeof years === 'number' && years >= 15) {
    seniorityRule =
      `User has ${years} years experience as ${role}. Default to Senior Manager, Director, VP, Head of, Principal roles. ` +
      'BUT if the user explicitly searches for a different domain/technology (e.g. "java jobs", ".net developer"), respect their intent and search for THAT role.';
  } else if (typeof years === 'number' && years >= 8) {
    seniorityRule =
      `User has ${years} years experience. Default to Senior+ roles. ` +
      'BUT if the user explicitly searches for a different role/tech stack, respect their intent.';
  }

  return [
    'JOB MATCHING GUARDRAILS:',
    `User role: ${role}. Total experience: ${years} years.`,
    seniorityRule,
    'job_search query MUST include the user\'s EXACT search terms. Add seniority/location from profile ONLY when user intent is vague.',
    'CRITICAL: If user says "find .NET jobs" or "show Java developer roles", put THOSE keywords in the query — do NOT substitute with profile role.',
  ].join('\n');
}

export function profileRoleAlignment(job: JobLike, profile: ProfileMatchContext): number {
  const userRole = profile.currentRole ?? profile.headline ?? '';
  return roleAlignmentScore(userRole, null, job.title);
}

/** Drop clear cross-domain mismatches (e.g. Product Owner profile → Software Engineer job). */
export function jobMatchesProfileRole(job: JobLike, profile: ProfileMatchContext): boolean {
  const align = profileRoleAlignment(job, profile);
  if (align !== 50 && align < 35) return false;

  const userFunc = classifyJobFunction(profile.currentRole ?? profile.headline);
  const jobFunc = classifyJobFunction(job.title);
  if (userFunc !== 'unknown' && jobFunc !== 'unknown' && userFunc !== jobFunc) {
    if (userFunc === 'product' && jobFunc === 'engineering') return false;
    if (userFunc === 'engineering' && jobFunc === 'product') return false;
    if (userFunc === 'design' && jobFunc === 'engineering') return false;
  }
  return true;
}

export function extractQueryMatchTokens(
  query: string,
  profile?: ProfileMatchContext | null,
): { roleTokens: string[]; locationTokens: string[] } {
  const q = query.toLowerCase();
  const roleTokens: string[] = [];
  const locationTokens: string[] = [];

  if (/\bproduct owner\b/.test(q)) roleTokens.push('product owner', 'product', 'owner');
  if (/\bproduct manager\b/.test(q)) roleTokens.push('product manager', 'product', 'manager');
  if (/\bprogram manager\b/.test(q)) roleTokens.push('program manager', 'program');
  if (/\bscrum master\b/.test(q)) roleTokens.push('scrum master', 'scrum');
  if (/\bsoftware engineer\b/.test(q)) roleTokens.push('software engineer', 'engineer');
  if (/\bdata (scientist|analyst)\b/.test(q)) roleTokens.push('data');

  for (const token of q.replace(/[^a-z0-9.\s]/g, ' ').split(/\s+/)) {
    if (token.length <= 2 || QUERY_STOP.has(token)) continue;
    if (LOCATION_ALIASES[token] || token === 'remote') {
      locationTokens.push(token);
      continue;
    }
    if (/\b(product|owner|manager|engineer|developer|designer|analyst|architect|consultant)\b/.test(token)) {
      roleTokens.push(token);
    }
  }

  if (profile?.location && locationTokens.length === 0) {
    const city = profile.location.split(',')[0]?.trim().toLowerCase();
    if (city) locationTokens.push(city);
  }

  const userRole = profile?.currentRole ?? profile?.headline ?? '';
  if (roleTokens.length === 0 && userRole) {
    const userFunc = classifyJobFunction(userRole);
    if (userFunc === 'product') roleTokens.push('product', 'owner', 'manager');
    else {
      roleTokens.push(
        ...userRole.toLowerCase().split(/\s+/).filter((t) => t.length > 2 && !QUERY_STOP.has(t)).slice(0, 4),
      );
    }
  }

  return {
    roleTokens: [...new Set(roleTokens)],
    locationTokens: [...new Set(locationTokens)],
  };
}

function jobLocationMatchesToken(jobLocation: string, token: string): boolean {
  const loc = jobLocation.toLowerCase();
  const terms = LOCATION_ALIASES[token] ?? [token];
  return terms.some((t) => loc.includes(t));
}

export function jobMatchesSearchQuery(
  job: JobLike,
  query: string,
  profile?: ProfileMatchContext | null,
): boolean {
  const { roleTokens, locationTokens } = extractQueryMatchTokens(query, profile);
  const title = (job.title ?? '').toLowerCase();
  const body = `${job.description_md ?? job.description ?? ''}`.toLowerCase();
  const loc = (job.location ?? '').toLowerCase();

  if (roleTokens.length > 0) {
    const roleHit = roleTokens.some((rt) => {
      if (rt.includes(' ')) return title.includes(rt) || body.includes(rt.slice(0, 12));
      return title.includes(rt) || (rt.length >= 5 && body.includes(rt));
    });
    if (!roleHit) return false;
  }

  if (locationTokens.length > 0 && loc) {
    const locHit = locationTokens.some((lt) => jobLocationMatchesToken(loc, lt));
    if (!locHit && !/\bremote\b/.test(loc) && !/\bremote\b/.test(body)) return false;
  }

  return true;
}

/**
 * @param explicitSearchQuery When provided and contains explicit role keywords
 * that differ from profile, role-alignment filter is skipped so users can
 * freely explore roles outside their profile domain.
 */
export function filterJobsByProfileRelevance<T extends JobLike>(
  jobs: T[],
  profile: ProfileMatchContext | null,
  explicitSearchQuery?: string,
): T[] {
  if (!profile) return jobs;

  const role = profile.currentRole?.trim() || profile.headline?.trim() || '';
  const skipRoleFilter = Boolean(
    explicitSearchQuery && userIntentDiffersFromProfile(explicitSearchQuery, role),
  );

  const minScore =
    profile.experienceYears != null && profile.experienceYears >= 10 ? 35 :
    profile.experienceYears != null && profile.experienceYears >= 6 ? 30 : 25;

  return jobs.filter((job) => {
    if (!jobMeetsSeniorityFloor(job, profile)) return false;
    if (!skipRoleFilter && !jobMatchesProfileRole(job, profile)) return false;
    if (job.matchScore != null && job.matchScore < minScore) return false;
    return true;
  });
}

/** Filter + rank agent job panel results by profile role and chat query. */
export function refineAgentJobResults<T extends JobLike>(
  jobs: T[],
  query: string,
  profile: ProfileMatchContext | null,
): T[] {
  if (!profile) return jobs;

  let filtered = filterJobsByProfileRelevance(jobs, profile, query);
  const { roleTokens } = extractQueryMatchTokens(query, profile);
  const hasSpecificRoleQuery = roleTokens.some((t) =>
    ['product', 'owner', 'manager', 'engineer', 'developer', 'designer', 'analyst', 'program', 'scrum'].includes(t),
  );

  if (hasSpecificRoleQuery) {
    const byQuery = filtered.filter((j) => jobMatchesSearchQuery(j, query, profile));
    if (byQuery.length >= 2) filtered = byQuery;
  }

  const role = profile.currentRole?.trim() || profile.headline?.trim() || '';
  const isExplicitDifferentSearch = query && userIntentDiffersFromProfile(query, role);

  return [...filtered].sort((a, b) => {
    if (isExplicitDifferentSearch) {
      return (b.matchScore ?? 0) - (a.matchScore ?? 0);
    }
    const scoreA = (a.matchScore ?? 0) * 0.55 + profileRoleAlignment(a, profile) * 0.45;
    const scoreB = (b.matchScore ?? 0) * 0.55 + profileRoleAlignment(b, profile) * 0.45;
    return scoreB - scoreA;
  });
}
